import { Link } from "react-router-dom";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import WorkCard from "@/components/base/WorkCard";
import SectionHeading from "@/components/base/SectionHeading";
import { works } from "@/mocks/works";
import { news } from "@/mocks/news";
import { siteInfo } from "@/mocks/site";
import { useSeo } from "@/hooks/useSeo";

const categories = Array.from(new Set(works.map((w) => w.category)));

const stats = [
  { value: "12+", label: "收录影视作品" },
  { value: "6", label: "作品类型" },
  { value: "8", label: "资讯与专访" },
  { value: "2019", label: "最早作品年份" },
];

export default function Home() {
  useSeo({
    title: "天海翼影视作品与明星资讯大全",
    description:
      "天海翼影视内容站为您提供天海翼的详细资料、影视作品介绍及相关资讯，覆盖最新动态和经典作品。",
    keywords: "天海翼,天海翼影视,天海翼明星资料,天海翼作品,影视资讯,影视明星,影视剧介绍",
  });

  const featured = [...works].sort((a, b) => b.popularity - a.popularity).slice(0, 8);
  const latestNews = news.slice(0, 5);
  const ranking = [...works].sort((a, b) => b.popularity - a.popularity).slice(0, 5);

  return (
    <>
      <HeroCarousel />

      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto grid max-w-[1280px] grid-cols-2 gap-6 px-4 py-10 md:grid-cols-4 md:px-6">
          {stats.map((item) => (
            <div key={item.label} className="text-center md:text-left">
              <p className="font-heading text-2xl font-bold text-primary-700 md:text-3xl">{item.value}</p>
              <p className="mt-1 text-xs text-foreground-600 md:text-sm">{item.label}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-[1280px] px-4 py-14 md:px-6 md:py-20">
        <SectionHeading
          eyebrow="Featured Works"
          title="影视作品精选"
          description="从天海翼的代表作到近年新剧，按人气为你整理了最值得一看的作品。"
          action={
            <Link
              to="/works"
              className="inline-flex shrink-0 cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
            >
              查看全部作品
              <i className="ri-arrow-right-line" />
            </Link>
          }
        />
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 md:gap-5">
          {featured.map((work, index) => (
            <WorkCard key={work.id} work={work} index={index} />
          ))}
        </div>

        <div className="mt-10 flex flex-wrap gap-2">
          {categories.map((cat) => (
            <Link
              key={cat}
              to={`/works?category=${encodeURIComponent(cat)}`}
              className="cursor-pointer whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-4 py-1.5 text-sm text-foreground-700 transition-colors hover:border-accent-300 hover:bg-accent-50 hover:text-accent-700"
            >
              {cat}
            </Link>
          ))}
        </div>
      </section>

      <section className="border-y border-background-200 bg-background-100">
        <div className="mx-auto max-w-[1280px] px-4 py-14 md:px-6 md:py-20">
          <SectionHeading
            eyebrow="Latest News"
            title="明星资讯快览"
            description="最新动态、深度专访与活动现场，第一时间了解天海翼的最新消息。"
            action={
              <Link
                to="/news"
                className="inline-flex shrink-0 cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                全部资讯
                <i className="ri-arrow-right-line" />
              </Link>
            }
          />
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
            <div className="flex flex-col gap-4 lg:col-span-2">
              {latestNews.map((item, index) => (
                <Link
                  key={item.id}
                  to={`/news/${item.id}`}
                  className="group flex animate-fade-up flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300 sm:flex-row cursor-pointer"
                  style={{ animationDelay: `${index * 60}ms` }}
                >
                  <div className="w-full shrink-0 overflow-hidden rounded-md bg-secondary-100 sm:w-44">
                    <div className="aspect-[8/5] w-full">
                      <img
                        src={item.cover}
                        alt={`${item.title} 配图`}
                        title={`天海翼 ${item.title}`}
                        className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                  </div>
                  <div className="flex min-w-0 flex-1 flex-col justify-center">
                    <div className="mb-2 flex flex-wrap items-center gap-2 text-xs text-foreground-500">
                      <span className="rounded-md bg-accent-100 px-2 py-0.5 text-[11px] text-accent-700">
                        {item.type}
                      </span>
                      <span>{item.date}</span>
                    </div>
                    <h3 className="font-heading text-base font-semibold text-foreground-950 line-clamp-2 transition-colors group-hover:text-primary-700 md:text-lg">
                      {item.title}
                    </h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-foreground-600 clamp-2">{item.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>

            <aside className="lg:col-span-1">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h3 className="mb-4 flex items-center gap-2 font-heading text-base font-semibold text-foreground-950">
                  <i className="ri-fire-line text-accent-500" />
                  作品人气榜
                </h3>
                <ol className="flex flex-col">
                  {ranking.map((work, index) => (
                    <li key={work.id}>
                      <Link
                        to={`/works/${work.id}`}
                        className="group flex cursor-pointer items-center gap-3 border-b border-background-100 py-3 last:border-b-0"
                      >
                        <span
                          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md font-heading text-sm font-bold ${
                            index < 3 ? "bg-primary-500 text-background-50" : "bg-background-200 text-foreground-600"
                          }`}
                        >
                          {index + 1}
                        </span>
                        <span className="flex min-w-0 flex-1 flex-col">
                          <span className="truncate text-sm font-medium text-foreground-900 transition-colors group-hover:text-primary-700">
                            {work.title}
                          </span>
                          <span className="text-xs text-foreground-500">
                            {work.year} · {work.category}
                          </span>
                        </span>
                        <span className="shrink-0 text-xs text-accent-600">{work.popularity}</span>
                      </Link>
                    </li>
                  ))}
                </ol>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1280px] px-4 py-14 md:px-6 md:py-20">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
          <div>
            <span className="mb-3 inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-accent-600">
              <span className="h-1.5 w-1.5 rounded-full bg-accent-500" />
              About The Site
            </span>
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">关于天海翼影视内容站</h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">{siteInfo.intro}</p>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600">
              我们坚持内容整理的清晰与真实，所有作品与资讯均与详情页一一对应。本站为影视资讯演示站点，不提供播放、下载、会员等任何功能性服务。
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/about"
                className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                了解本站
              </Link>
              <Link
                to="/search"
                className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-6 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                站内搜索
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {[
              { icon: "ri-movie-2-line", title: "作品全览", desc: "按类型、年份、状态多条件筛选" },
              { icon: "ri-newspaper-line", title: "资讯动态", desc: "新闻、采访、活动现场一网打尽" },
              { icon: "ri-links-line", title: "深度关联", desc: "作品与资讯互相链接，脉络清晰" },
              { icon: "ri-share-forward-line", title: "便捷分享", desc: "一键复制链接与分享到社交平台" },
            ].map((item) => (
              <div key={item.title} className="rounded-lg border border-background-200 bg-background-100 p-5">
                <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-md bg-primary-100">
                  <i className={`${item.icon} text-xl text-primary-700`} />
                </div>
                <h3 className="font-heading text-base font-semibold text-foreground-950">{item.title}</h3>
                <p className="mt-1.5 text-xs leading-relaxed text-foreground-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}